'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';

import { LINE, RADIUS, SHADOW, SURFACE } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

/**
 * 허브에서 **누를 수 있는 줄**의 한 벌 — 순위 줄 · 이웃 브랜드 · 계정 줄이 같은 결을 쓴다.
 *
 * 🔑 누를 수 있다는 것은 **마우스를 올리기 전에** 보여야 한다 — 그래서 옅은 면과 실선을 늘 둘러 두고,
 *   오른쪽 꺾쇠는 평소 흐리게 있다가 올리면 또렷해지며 살짝 나아간다(있다가 생기면 줄 폭이 흔들린다).
 * ⚠️ 못 누르는 줄(자기 자신·값만 보는 줄)은 `disabled` 로 둔다 — 같은 모양에 손짓만 뺀다.
 * ⚠️ 테두리 색만 바꾼다 — 배경까지 물들이면 안에 든 막대·썸네일의 색이 흐려진다.
 * 🔑 **열 줄 넘게 쌓는 자리는 `flat`** — 줄마다 카드를 두르면 테두리가 스무 겹이 되어 탑이 된다.
 *   그때는 카드를 벗고 **구분선 하나**로만 가른다(목록의 결은 부르는 쪽이 감싼다).
 *   ⚠️ `flat` 에는 **꺾쇠도 두지 않는다** — 줄이 다 눌리는 목록에서 줄마다 꺾쇠를 두면 같은 표가
 *     열 개 서서 아무것도 가리키지 못하고, 그 자리만큼 썸네일이 밀린다. 누를 수 있다는 것은
 *     면 색(hover)과 손짓이 말한다.
 */
export function HubPressable({
  onPress,
  disabled = false,
  dense = false,
  flat = false,
  children,
}: {
  onPress?: () => void;
  /** 누를 수 없는 줄 — 모양은 같고 손짓과 꺾쇠만 없다 */
  disabled?: boolean;
  /** 촘촘한 목록(오른쪽 칸)의 여백 */
  dense?: boolean;
  /**
   * 카드를 벗는다 — **길게 쌓는 목록**용(테두리가 겹겹이 서면 읽히지 않는다).
   * ⚠️ 누를 수 있다는 표시가 테두리에서 **면 색**으로 옮겨간다 — 꺾쇠는 그대로 둔다.
   */
  flat?: boolean;
  children: ReactNode;
}) {
  const pressable = Boolean(onPress) && !disabled;

  return (
    <Box
      role={pressable ? 'button' : undefined}
      tabIndex={pressable ? 0 : undefined}
      onClick={pressable ? onPress : undefined}
      onKeyDown={
        pressable
          ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onPress?.();
            }
          }
          : undefined
      }
      sx={{
        position: 'relative',
        px: dense ? 1 : 1.25,
        py: dense ? 0.75 : 1,
        pr: pressable && !flat ? (dense ? 2.5 : 3) : undefined,
        borderRadius: RADIUS.md,
        ...(flat
          ? { border: 'none', bgcolor: 'transparent' }
          : { border: '1px solid', borderColor: LINE.edge, bgcolor: 'background.paper' }),
        ...(pressable && {
          cursor: 'pointer',
          transition: (theme) => theme.transitions.create(['border-color', 'box-shadow', 'transform', 'background-color'], { duration: 120 }),
          '&:hover': flat
            ? { bgcolor: SURFACE.hover }
            : {
              borderColor: 'primary.main',
              boxShadow: SHADOW.raised,
              transform: 'translateY(-1px)',
            },
          '&:hover .hub-pressable__go': { opacity: 1, transform: 'translate(2px, -50%)' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
        }),
      }}
    >
      {children}

      {pressable && !flat && (
        <Iconify
          className="hub-pressable__go"
          icon="solar:alt-arrow-right-linear"
          width={14}
          sx={{
            position: 'absolute',
            right: dense ? 6 : 8,
            top: '50%',
            transform: 'translate(0, -50%)',
            opacity: 0.28,
            color: 'text.secondary',
            pointerEvents: 'none',
            transition: (theme) => theme.transitions.create(['opacity', 'transform'], { duration: 120 }),
          }}
        />
      )}
    </Box>
  );
}
